import { Component, OnInit, Input, Output, EventEmitter, forwardRef, ChangeDetectorRef, HostListener, ElementRef } from '@angular/core';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs/Rx';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';
import { ParameterService } from '../../utils/services/parameter.service';
import { environment } from '../../../environments/environment';

declare const google: any;

@Component({
  selector: 'app-google-autocomplete-input',
  templateUrl: './google-autocomplete-input.component.html',
  styleUrls: ['./google-autocomplete-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GoogleAutocompleteInputComponent),
      multi: true
    }
  ]
})
export class GoogleAutocompleteInputComponent implements OnInit, ControlValueAccessor {

  search: FormControl;
  selectedCity = null;
  isPanelVisible = false;
  isDataLoaded = false;

  @Input() validateCityFromParameter = true;
  @Input() gMapElement;

  @Output() onCitySelected: EventEmitter<any> = new EventEmitter();

  validatedBiziCities = [];
  parameterValidationSubscription: Subscription = null;

  public propagateChange = (_: any) => {};

  private autocompleteService: any;
  private placesService: any;

  @HostListener('window:click', ['$event'])
  clickHandler(event) {
    if (!(this.elementRef.nativeElement.contains(event.target))) {
      this.isPanelVisible = false;
    }
  }

  constructor(
    private parameter: ParameterService,
    private elementRef: ElementRef,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.search = new FormControl();

    this.waitForGoogleMaps().then(() => {
      this.autocompleteService = new google.maps.places.AutocompleteService();
      const dummyDiv = document.createElement('div');
      this.placesService = new google.maps.places.PlacesService(dummyDiv);

      this.search.valueChanges.debounceTime(400).distinctUntilChanged().subscribe(val => {
        if (!val || val.length < 3 || (this.selectedCity && (this.selectedCity.inputPlace == val || this.selectedCity.desc == val))) {
          return;
        }

        this.isPanelVisible = true;
        this.isDataLoaded = false;
        this.validatedBiziCities = [];

        this.autocompleteService.getPlacePredictions(
          { input: val, types: ['(cities)'] },
          (predictions, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
              this.isDataLoaded = true;
              this.cd.detectChanges();
              return;
            }

            let detailCalls = 0;
            let needValidateCities = [];
            let placePredictionList = [];

            predictions.forEach((prediction) => {
              detailCalls++;
              this.placesService.getDetails(
                { placeId: prediction.place_id, fields: ['address_components', 'geometry', 'name'] },
                (place, detailStatus) => {
                  detailCalls--;

                  if (detailStatus === google.maps.places.PlacesServiceStatus.OK && place) {
                    const addressComponents = place.address_components || [];
                    const cityName = place.name || this.getAddressComponent(addressComponents, 'locality') || this.getAddressComponent(addressComponents, 'administrative_area_level_1');
                    const countryComponent = addressComponents.find(c => c.types.includes('country'));
                    const countryCode = countryComponent ? countryComponent.short_name : null;
                    const countryName = countryComponent ? countryComponent.long_name : null;
                    const lat = place.geometry ? place.geometry.location.lat() : 0;
                    const lng = place.geometry ? place.geometry.location.lng() : 0;

                    const highlightedDesc = this.highlightMatch(prediction.description, val);

                    placePredictionList.push({
                      title: highlightedDesc,
                      rawTitle: prediction.description,
                      searchField: prediction.structured_formatting ? prediction.structured_formatting.main_text : prediction.description,
                      position: { lat, lng }
                    });

                    needValidateCities.push({
                      name: cityName,
                      countryCode: countryCode,
                      countryName: countryName
                    });
                  }

                  if (detailCalls === 0) {
                    this.validateAutoCompleteCities(needValidateCities, placePredictionList);
                  }
                }
              );
            });
          }
        );
      });
    });
  }

  private waitForGoogleMaps(): Promise<void> {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }

  private getAddressComponent(components: any[], type: string): string {
    const comp = components.find(c => c.types.includes(type));
    return comp ? comp.long_name : null;
  }

  private highlightMatch(text: string, query: string): string {
    if (!text || !query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.replace(regex, '<b>$1</b>');
  }

  validateAutoCompleteCities(autoCompleteCityList, placePredictionList) {
    let parameterReqData = autoCompleteCityList;
    if (this.parameterValidationSubscription) {
      this.parameterValidationSubscription.unsubscribe();
      this.validatedBiziCities = [];
    }
    this.parameterValidationSubscription = this.parameter.getHotelCities({ data: parameterReqData }).subscribe(paramRes => {
      let validatedCities = paramRes.data;

      autoCompleteCityList.forEach((item, index) => {
        if (this.validateCityFromParameter) {
          if (validatedCities[index].result) {
            this.validatedBiziCities.push({
              cityId: validatedCities[index].result,
              desc: placePredictionList[index].title,
              rawDesc: placePredictionList[index].rawTitle,
              longDesc: placePredictionList[index].rawTitle,
              inputPlace: placePredictionList[index].searchField,
              cityName: autoCompleteCityList[index].name,
              countryCode: autoCompleteCityList[index].countryCode,
              countryName: autoCompleteCityList[index].countryName,
              position: placePredictionList[index].position
            });
          }
        } else {
          this.validatedBiziCities.push({
            cityId: validatedCities[index].result,
            desc: placePredictionList[index].title,
            rawDesc: placePredictionList[index].rawTitle,
            longDesc: placePredictionList[index].rawTitle,
            inputPlace: placePredictionList[index].searchField,
            cityName: autoCompleteCityList[index].name,
            countryCode: autoCompleteCityList[index].countryCode,
            countryName: autoCompleteCityList[index].countryName,
            position: placePredictionList[index].position
          });
        }
      });

      this.isDataLoaded = true;
      this.cd.detectChanges();
    });
  }

  writeValue(obj: any): void {
    this.selectedCity = {};
    this.selectedCity['desc'] = obj;
    if (this.search) {
      this.search.patchValue(obj);
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  onListItemClicked(index) {
    this.selectedCity = this.validatedBiziCities[index];
    this.search.patchValue(this.validatedBiziCities[index].inputPlace);
    this.isPanelVisible = false;
    this.onCitySelected.emit({ item: this.selectedCity });
    this.propagateChange({ item: this.selectedCity });
  }
}



// WEBPACK FOOTER //
// /var/www/bizisystem-frontend/src/src/app/shared/google-autocomplete-input/google-autocomplete-input.component.ts